import React, {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ApolloClient, DocumentNode, useApolloClient } from '@apollo/client';
import _ from 'lodash';
import { useCurrency, useI18n, useLogger, useSelector } from '~Hooks';
import { useEntryFinder } from '~Components/EntryFinder';
import { useInventoryUniverse } from '~Components/InventoryUniverse';
import { checkOutOfStock } from '~Scenes/SelfServiceKiosks/utils/inventory';
import { ORDER_STATUS, SERVICE_TYPE } from '~../__queries__/globalTypes';
import { getExpressMenus_allAppliedMenus_menu_menuEntries as MenuEntry } from '~Components/MenuManager/__queries__';
import { useExpressCartSummary, useExpressCartActions } from '../ExpressCartProvider';
import { EposMenuCategory, EposMenuItem, EposMenuUpsell } from '../../types';
import { categoryIconSrc } from '../../Utils/categoryIcons';
import { preloadItemImages } from '../../Utils/preloadImages';
import {
  CompleteExpressQueryEpos,
  CompleteExpressQueryEpos_company_categories,
  CompleteExpressQueryEpos_company_categories_items,
} from './api/__queries__';
import COMPLETE_QUERY from './api/completeQuery';
import {
  EPOS_CATEGORIES_QUERY,
  EPOS_ITEMS_QUERY,
  EPOS_MODIFICATION_GROUPS_QUERY,
  EPOS_VARIATIONS_QUERY,
  EPOS_VENUE_QUERY,
} from './api/queries';

type ExpressCategory = CompleteExpressQueryEpos_company_categories;
type ExpressItem = CompleteExpressQueryEpos_company_categories_items;

/** Items tagged [m] in their title are staff-only and never shown to shoppers. */
const hasStaffOnlyMarker = (title: string) => title.includes('[m]');

interface ExpressProductLibraryContextType {
  /** True until every staged query has resolved. */
  loading: boolean;
  /** 0-100, for the initial loader. */
  progress: number;
  /** Human readable description of the current loading stage. */
  progressText: string;
  /** True once the venue resource has loaded. False after a failed load. */
  hasData: boolean;
  /** The company's default status for new orders - goes on every order EPOS creates. */
  defaultOrderStatus: ORDER_STATUS | null;

  /**
   * What the menu renders: categories a shopper may order from (not deleted,
   * not hidden from customer flows, at least one item in the menu), with
   * items already translated, priced for the current service type, and
   * carrying their in-menu upsells. Sorted by `order`, highest first.
   */
  menuCategories: EposMenuCategory[];
  /** Ids of menu items that can't be added right now - out of stock, or every unit already in the cart. */
  outOfStockIds: Set<string>;
  /** Category id -> ids of its menu items. Promo codes limited to categories resolve through this. */
  categoryItemIds: Record<string, string[]>;
  /** Category id + UK VAT rate, used for take-away VAT calculations. */
  categoriesWithUkVatRate: { id: string; ukVatRate: number }[];
  /** Item id -> render-ready menu item. Editing a cart line reopens ItemDetail through this. */
  menuItemMap: Record<string, EposMenuItem>;
}

/** One cross-sell in the cart drawer: an upsell item and the cart line it would attach to. */
export interface ExpressCartSuggestion {
  /** Unique per (parent line, item) so the same item can be offered for two parents. */
  key: string;
  item: EposMenuItem;
  parentCartId: string;
}

const ExpressProductLibraryContext = createContext<ExpressProductLibraryContextType | undefined>(
  undefined,
);

/**
 * Separate context because it changes on every cart change - only the cart
 * drawer's suggestion rail subscribes to it, nothing else pays for it.
 */
const ExpressCartSuggestionsContext = createContext<ExpressCartSuggestion[] | undefined>(
  undefined,
);

/**
 * Cross-sells for the cart drawer: the items of the *multi-select* upsell
 * groups of the lines in the cart, in stock, one entry per item - the first
 * parent line is the one it attaches to. Multi-select means more than one may
 * be taken, so an item stays offered after it has been added; the rail only
 * changes when lines come and go. Single-select groups are a choice made in
 * the detail flow and are never re-offered here.
 */
export const useExpressCartSuggestions = (): ExpressCartSuggestion[] => {
  const context = useContext(ExpressCartSuggestionsContext);
  if (!context) {
    throw new Error(
      'useExpressCartSuggestions must be used within an ExpressProductLibraryProvider',
    );
  }
  return context;
};

/** Same ids as before -> same Set reference, so memoized consumers stay put. */
const sameIds = (a: Set<string>, b: Set<string>) =>
  a.size === b.size && Array.from(a).every((id) => b.has(id));

/**
 * Non-throwing variant for hooks that may run above this provider - the cart
 * hook is used inside this very provider, so it can't throw.
 */
export const useOptionalExpressProductLibrary = () => useContext(ExpressProductLibraryContext);

export const useExpressProductLibrary = (): ExpressProductLibraryContextType => {
  const context = useContext(ExpressProductLibraryContext);
  if (!context) {
    throw new Error(
      'useExpressProductLibrary must be used within an ExpressProductLibraryProvider',
    );
  }
  return context;
};

/** Runs a query, retrying up to five times with a growing delay. Resolves null on final failure. */
function queryAndRetry(
  client: ApolloClient<any>,
  query: DocumentNode,
  variables: object | null,
  iteration = 0,
): Promise<unknown> {
  function onFail() {
    return new Promise((resolve, reject) => {
      if (iteration >= 5) {
        resolve(null);
        return;
      }

      setTimeout(() => {
        queryAndRetry(client, query, variables, iteration + 1)
          .then(resolve)
          .catch(reject);
      }, iteration * 250);
    });
  }

  return client
    .query({ query, variables: variables || {} })
    .then((response) => {
      if (!response.data) {
        return onFail();
      }
      return response;
    })
    .catch(onFail);
}

interface Props {
  venueId: string;
  children: ReactNode;
}

/**
 * Downloads the venue's whole product catalogue up front and exposes it,
 * together with the "is this item in the menu" logic, to the EPOS flow.
 *
 * Menu membership comes from state.POSMenu, exactly like SelfServiceKiosks:
 * EPOS mounts the kiosk's MenuManager (Components/MenuManagerBridge) to keep
 * POSMenu on the menu for the service type and time, and this provider
 * reads it back through useEntryFinder - the same lookup SelfServiceKiosks
 * itself uses to decide whether an item is orderable.
 */
export const ExpressProductLibraryProvider: FC<Props> = ({ venueId, children }) => {
  const client = useApolloClient();
  const logger = useLogger('epos-product-library');
  const { i18n } = useI18n();
  const currency = useCurrency();
  const inventory = useInventoryUniverse();
  const findEntryFromItem = useEntryFinder();
  /** Items/service type only - doesn't re-render for a membership lookup or pickup time change. */
  const expressCart = useExpressCartSummary();
  const expressCartActions = useExpressCartActions();

  const takeAwayMenu = useSelector((state) => state.POSTakeAwayMenu);

  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState(`${i18n('App.Loading')}...`);
  const [data, setData] = useState<CompleteExpressQueryEpos | null>(null);

  /** Loads the venue resource in stages, reporting progress along the way. */
  const runQuery = useCallback(async () => {
    const randomOffset = () => Math.round(Math.random() * 9);
    /** Stage label, progress and query for each step, run in order. */
    const stages: { text: string; progress: number; query: DocumentNode }[] = [
      { text: 'SSK.LoadingVariations', progress: 10, query: EPOS_VARIATIONS_QUERY },
      { text: 'SSK.LoadingVariations', progress: 20, query: EPOS_MODIFICATION_GROUPS_QUERY },
      { text: 'SSK.LoadingVenue', progress: 30, query: EPOS_VENUE_QUERY },
      { text: 'SSK.LoadingItems', progress: 65, query: EPOS_ITEMS_QUERY },
      { text: 'SSK.LoadingCategories', progress: 80, query: EPOS_CATEGORIES_QUERY },
    ];

    logger.info('Loading product library', { venueId });

    try {
      for (const stage of stages) {
        setProgress(stage.progress + randomOffset());
        setProgressText(`${i18n(stage.text)}...`);
        await queryAndRetry(client, stage.query, { venueId });
      }

      // Everything is in the cache now, so this resolves immediately.
      const response = client.readFragment<CompleteExpressQueryEpos>({
        fragment: COMPLETE_QUERY,
        id: venueId,
        fragmentName: 'CompleteExpressQueryEpos',
      });

      setProgress(100);
      setData(response);
      setLoading(false);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      logger.error('Failed to load product library', { venueId, errorMessage });
      setProgressText((text) => `${text} ${errorMessage}`);
    }
  }, [client, venueId]);

  useEffect(() => {
    runQuery();
  }, [runQuery]);

  /** Item id -> menu entry for the take-away menu, empty if none is applied. */
  const takeAwayEntryMap = useMemo(() => {
    if (!takeAwayMenu) {
      return {};
    }
    return _.keyBy(takeAwayMenu.menuEntries, (entry) => entry.item.id);
  }, [takeAwayMenu]);

  const isItemInMenu = useCallback((itemId: string) => !!findEntryFromItem(itemId), [
    findEntryFromItem,
  ]);

  const findMenuEntry = useCallback(
    (itemId: string, forServiceType?: SERVICE_TYPE | null) => {
      if (forServiceType === SERVICE_TYPE.TAKE_AWAY && takeAwayEntryMap[itemId]) {
        return takeAwayEntryMap[itemId];
      }
      return findEntryFromItem(itemId);
    },
    [findEntryFromItem, takeAwayEntryMap],
  );

  const rawCategories = useMemo(() => data?.company?.categories ?? [], [data]);

  /** Whether a shopper may see this item: it is in the menu and not staff-only. */
  const isOrderable = useCallback(
    (item: ExpressItem) => !hasStaffOnlyMarker(i18n(item.titleLang)) && isItemInMenu(item.id),
    [isItemInMenu],
  );

  const categories = useMemo(() => {
    const filtered = rawCategories
      .filter((category) => !category.deleted && !category.hideInCustomerOrderingFlows)
      .map((category) => ({ ...category, items: category.items.filter(isOrderable) }))
      .filter((category) => category.items.length > 0)
      .sort((a, b) => b.order - a.order);

    logger.info('Filtered categories against menu', {
      total: rawCategories.length,
      visible: filtered.length,
    });

    return filtered;
  }, [rawCategories, isOrderable]);

  const allItems = useMemo(() => {
    const items = _.flatMap(categories, 'items') as ExpressItem[];
    const upsellItems = (_.flatMap(_.flatMap(items, 'upsales'), 'items') as ExpressItem[]).filter(
      Boolean,
    );
    return _.uniqBy([...items, ...upsellItems], 'id');
  }, [categories]);

  const itemCategoryMap = useMemo(
    () =>
      categories.reduce((map, category) => {
        category.items.forEach((item) => {
          map[item.id] = { id: category.id, name: i18n(category.nameLang) };
        });
        return map;
      }, {} as Record<string, { id: string; name: string }>),
    [categories],
  );

  const categoriesWithUkVatRate = useMemo(
    () => rawCategories.map((c) => ({ id: c.id, ukVatRate: c.ukVatRate })),
    [rawCategories],
  );

  // The cart resolves take-away VAT inside addMenuItem/addUpsell from this list.
  useEffect(() => {
    expressCartActions.registerUkVatCategories(categoriesWithUkVatRate);
  }, [categoriesWithUkVatRate, expressCartActions]);

  const preloadedRef = useRef(false);
  // The kiosk stays on the menu all day without a restart, so it's worth
  // warming the image cache once up front - every item/category photo, at
  // every size it's ever rendered at - instead of each context (menu tile,
  // cart line, detail pane...) paying a fresh CDN fetch the first time it
  // shows up. Runs once per boot; `allItems`/`rawCategories` don't change
  // when the shopper only switches service type.
  useEffect(() => {
    if (preloadedRef.current || !allItems.length) {
      return;
    }
    preloadedRef.current = true;
    preloadItemImages([
      ...allItems.map((item) => item.image?.file.url ?? ''),
      ...rawCategories.map((category) => category.image?.file.url ?? ''),
    ]);
  }, [allItems, rawCategories]);

  /** Indexed once per menu load - `menuCategories` looks up every upsell item through this. */
  const itemsById = useMemo(() => _.keyBy(allItems, 'id'), [allItems]);

  const findItem = useCallback((itemId: string) => itemsById[itemId], [itemsById]);

  /**
   * The render-ready menu. Built once per menu/service-type change, never in a
   * component - views only read `menuCategories`.
   */
  const menuCategories = useMemo<EposMenuCategory[]>(() => {
    /** Library item -> menu item. `withUpsells` is false for upsell items themselves, so it never recurses. */
    const toMenuItem = (
      item: ExpressItem,
      categoryId: string,
      withUpsells: boolean,
    ): EposMenuItem | null => {
      const entry = findMenuEntry(item.id, expressCart.serviceType);
      if (!entry) {
        return null;
      }

      const upsells: EposMenuUpsell[] = withUpsells
        ? item.upsales
            .filter((upsell) => !upsell.hideInEPOS)
            .map((upsell) => {
              const order = upsell.itemsSortingOrder ?? [];
              const items = upsell.items
                .map((ref) => findItem(ref.id))
                .filter((found): found is ExpressItem => !!found)
                .map((found) =>
                  toMenuItem(found, itemCategoryMap[found.id]?.id ?? categoryId, false),
                )
                .filter((found): found is EposMenuItem => !!found)
                .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
              return {
                id: upsell.id,
                name: i18n(upsell.name),
                enableMultiSelect: upsell.enableMultiSelect,
                iconSrc: categoryIconSrc(upsell.icon),
                items,
              };
            })
            .filter((upsell) => upsell.items.length > 0)
        : [];

      // Same rule as the kiosk's GeneralItem: nothing to ask about means the tap adds straight away.
      const instant = !!entry.instant || (!item.modificationGroups?.length && !upsells.length);

      return {
        id: item.id,
        categoryId,
        title: i18n(item.titleLang),
        description: i18n(item.descriptionLang),
        imageUrl: item.image?.file.url ?? '',
        price: currency.format(entry.price),
        priceValue: entry.price,
        vatRate: item.vatRate,
        ageRestricted: item.ageRestricted,
        modificationGroups: item.modificationGroups ?? [],
        allergyTags: item.allergyTags ?? [],
        instant,
        upsells,
      };
    };

    const built = categories.map((category) => ({
      id: category.id,
      name: i18n(category.nameLang),
      imageUrl: category.image?.file.url ?? '',
      iconSrc: categoryIconSrc(category.icon),
      order: category.order,
      items: category.items
        .map((item) => toMenuItem(item, category.id, true))
        .filter((item): item is EposMenuItem => !!item),
    }));

    return built;
  }, [categories, findMenuEntry, findItem, itemCategoryMap, expressCart.serviceType]);

  /**
   * Recomputed as stock events land or the cart changes. The previous Set is
   * kept whenever the ids are the same, so adding a burger does not hand every
   * MenuSection a new Set and re-render the whole menu.
   */
  const outOfStockIdsRef = useRef(new Set<string>());
  const outOfStockIds = useMemo(() => {
    const ids = new Set<string>();
    menuCategories.forEach((category) =>
      category.items.forEach((item) => {
        const outOfStock = checkOutOfStock({
          id: item.id,
          inventoryItems: inventory.items,
          disregardCount: false,
          cartItems: expressCart.items,
        });
        if (outOfStock) {
          ids.add(item.id);
        }
      }),
    );
    if (sameIds(ids, outOfStockIdsRef.current)) {
      return outOfStockIdsRef.current;
    }
    outOfStockIdsRef.current = ids;
    return ids;
  }, [menuCategories, inventory.items, expressCart.items]);

  const categoryItemIds = useMemo(() => {
    const map: Record<string, string[]> = {};
    menuCategories.forEach((category) => {
      map[category.id] = category.items.map((item) => item.id);
    });
    return map;
  }, [menuCategories]);

  /** Item id -> menu item, for the cart suggestions below. */
  const menuItemMap = useMemo(() => {
    const map: Record<string, EposMenuItem> = {};
    menuCategories.forEach((category) =>
      category.items.forEach((item) => {
        map[item.id] = item;
      }),
    );
    return map;
  }, [menuCategories]);

  /** key -> last suggestion object, so unchanged cards keep their props by reference. */
  const previousSuggestionsRef = useRef(new Map<string, ExpressCartSuggestion>());

  const cartSuggestions = useMemo<ExpressCartSuggestion[]>(() => {
    const suggestions: ExpressCartSuggestion[] = [];
    const previous = previousSuggestionsRef.current;
    const next = new Map<string, ExpressCartSuggestion>();
    /** Item ids already offered - the same product is never shown twice. */
    const offered = new Set<string>();
    const parents = expressCart.items.filter((line) => !line.parentId);

    parents.forEach((parent) => {
      const menuItem = menuItemMap[parent.itemId];
      if (!menuItem) {
        return;
      }
      menuItem.upsells
        .filter((upsell) => upsell.enableMultiSelect)
        .forEach((upsell) =>
          upsell.items.forEach((upsellItem) => {
            if (offered.has(upsellItem.id) || outOfStockIds.has(upsellItem.id)) {
              return;
            }
            offered.add(upsellItem.id);
            const key = `${parent.id}:${upsellItem.id}`;
            const old = previous.get(key);
            const suggestion =
              old && old.item === upsellItem
                ? old
                : { key, item: upsellItem, parentCartId: parent.id };
            next.set(key, suggestion);
            suggestions.push(suggestion);
          }),
        );
    });

    previousSuggestionsRef.current = next;
    logger.info('Built cart suggestions', { count: suggestions.length });
    return suggestions;
  }, [expressCart.items, menuItemMap, outOfStockIds]);

  const contextValue = useMemo<ExpressProductLibraryContextType>(
    () => ({
      loading,
      progress,
      progressText,
      hasData: !!data,
      defaultOrderStatus: data?.company?.defaultOrderStatus ?? null,
      menuCategories,
      outOfStockIds,
      categoryItemIds,
      categoriesWithUkVatRate,
      menuItemMap,
    }),
    [
      loading,
      progress,
      progressText,
      data,
      menuCategories,
      outOfStockIds,
      categoryItemIds,
      categoriesWithUkVatRate,
      menuItemMap,
    ],
  );

  return (
    <ExpressProductLibraryContext.Provider value={contextValue}>
      <ExpressCartSuggestionsContext.Provider value={cartSuggestions}>
        {children}
      </ExpressCartSuggestionsContext.Provider>
    </ExpressProductLibraryContext.Provider>
  );
};

export default ExpressProductLibraryProvider;
